/* External */
import {
  memo,
  useMemo,
  useState,
  useCallback,
  useEffect,
} from 'react';
import PropTypes from 'prop-types';
import sortedIndex from 'lodash-es/sortedIndex';
import debounce from 'lodash-es/debounce';

/* Components */
import ScrollView from '../ScrollView';
import VirtualizedItem from './VirtualizedItem';

function getElementIndexByScroll(scrollValue, itemSizeOrAccumulatedItemSizes) {
  let index;
  index = typeof itemSizeOrAccumulatedItemSizes === 'number'
    ? Math.ceil(scrollValue / itemSizeOrAccumulatedItemSizes)
    : sortedIndex(itemSizeOrAccumulatedItemSizes, scrollValue);

  index -= 1;
  return index < 0 ? 0 : index;
}

const TIME_TO_WAIT_TO_MOUNT_NEW_ITEMS = 100;

function VirtualizedList({
  className = '',
  style: styleProp = undefined,
  outerElementType: OuterElementType = ScrollView,
  height,
  showItems = true,
  itemCount,
  itemSize,
  overscanCount = 1,
  renderer,
  onScroll = undefined,
  children = undefined,
  resetScrollPosition = false,
  initialScrollPosition = 0,
  propagateEventOnReachTop = false,
  initialMaxOffset = 0,
  itemsContainerClassName = '',
}) {
  const [scrollPosition, setScrollPosition] = useState(initialScrollPosition);

  const accumulatedItemSizes = useMemo(() => {
    if (typeof itemSize === 'function') {
      const accumulatedItemSizesArray = [];
      accumulatedItemSizesArray.push(0);
      let accumulator = 0;
      for (let index = 0; index < itemCount; index += 1) {
        const sizeOfThisItem = itemSize(index);
        accumulator += sizeOfThisItem;
        accumulatedItemSizesArray.push(accumulator);
      }

      return accumulatedItemSizesArray;
    }

    return undefined;
  }, [itemCount, itemSize]);

  const scrollableHeight = typeof itemSize === 'function'
    ? accumulatedItemSizes[itemCount]
    : itemCount * itemSize;

  const firstVisibleElementIndex = useMemo(
    () => getElementIndexByScroll(scrollPosition, accumulatedItemSizes || itemSize),
    [accumulatedItemSizes, itemSize, scrollPosition],
  );

  const lastVisibleElementIndex = useMemo(
    () => getElementIndexByScroll(scrollPosition + height, accumulatedItemSizes || itemSize),
    [accumulatedItemSizes, height, itemSize, scrollPosition],
  );

  /* Windowed slice around the visible rows with a constant overscan, instead
     of rebuilding a Record for every row on each scroll tick: Home/Lander/
     EPG scroll is O(visible rows). */
  const virtualizedItemsToDisplay = useMemo(() => {
    if (!showItems || itemCount <= 0) {
      return [];
    }

    const sizeOf = (index) => (typeof itemSize === 'function'
      ? accumulatedItemSizes[index + 1] - accumulatedItemSizes[index]
      : itemSize);

    /* The overscan counts rows that take space. A zero-height row (an empty
       or collapsed slot) must not consume it, or the real neighbour of the
       visible range is unmounted and spatial navigation has nothing to move
       to: UP from the EPG's first row on Sports was dead because the lander's
       last row before the EPG is a 0px spacer that ate the single overscan
       row, so the carousel above it was gone. */
    let startIndex = Math.min(firstVisibleElementIndex, Math.max(itemCount - 1, 0));
    for (let remaining = overscanCount; remaining > 0 && startIndex > 0;) {
      startIndex -= 1;
      if (sizeOf(startIndex) > 0) remaining -= 1;
    }
    let endIndex = Math.min(lastVisibleElementIndex + 1, itemCount);
    for (let remaining = overscanCount; remaining > 0 && endIndex < itemCount;) {
      if (sizeOf(endIndex) > 0) remaining -= 1;
      endIndex += 1;
    }

    const items = [];
    for (let index = startIndex; index < endIndex; index += 1) {
      let position;
      let itemHeight;
      if (typeof itemSize === 'function') {
        itemHeight = accumulatedItemSizes[index + 1] - accumulatedItemSizes[index];
        position = accumulatedItemSizes[index];
      } else {
        itemHeight = itemSize;
        position = itemSize * index;
      }

      items.push(
        <VirtualizedItem
          key={index}
          index={index}
          position={position}
          height={itemHeight}
          renderer={renderer}
          firstVisibleElementIndex={firstVisibleElementIndex}
        />,
      );
    }
    return items;
  }, [
    accumulatedItemSizes,
    firstVisibleElementIndex,
    itemCount,
    itemSize,
    lastVisibleElementIndex,
    overscanCount,
    renderer,
    showItems,
  ]);

  const itemsContainerStyle = useMemo(() => ({
    position: 'relative',
    height: showItems ? `${scrollableHeight}px` : '0px',
  }), [scrollableHeight, showItems]);

  const debouncedSetScrollPosition = useMemo(
    () => debounce(setScrollPosition, TIME_TO_WAIT_TO_MOUNT_NEW_ITEMS, { leading: false }),
    [],
  );

  const handleScroll = useCallback(
    (newScrollPosition, maxScrollOffset) => {
      debouncedSetScrollPosition(newScrollPosition);
      if (onScroll) onScroll(newScrollPosition, maxScrollOffset);
    },
    [debouncedSetScrollPosition, onScroll],
  );

  const style = useMemo(() => ({
    ...styleProp,
    height,
  }), [height, styleProp]);

  useEffect(() => {
    const prevDebouncedSetScrollPosition = debouncedSetScrollPosition;
    return () => {
      prevDebouncedSetScrollPosition.cancel();
    };
  }, [debouncedSetScrollPosition]);

  return (
    <OuterElementType
      className={`dtv-html5-c-virtualized-list ${className}`}
      onScroll={handleScroll}
      resetScrollPosition={resetScrollPosition}
      style={style}
      initialScrollPosition={initialScrollPosition}
      propagateEventOnReachTop={propagateEventOnReachTop}
      initialMaxOffset={initialMaxOffset}
    >
      <div className={itemsContainerClassName} style={itemsContainerStyle}>
        {showItems ? virtualizedItemsToDisplay : undefined}
      </div>
      {children}
    </OuterElementType>
  );
}

VirtualizedList.propTypes = {
  className: PropTypes.string,
  itemsContainerClassName: PropTypes.string,
  style: PropTypes.shape({}),
  outerElementType: PropTypes.elementType,
  height: PropTypes.number.isRequired,
  showItems: PropTypes.bool,
  itemCount: PropTypes.number.isRequired,
  itemSize: PropTypes.oneOfType([
    PropTypes.number,
    PropTypes.func,
  ]).isRequired,
  overscanCount: PropTypes.number,
  renderer: PropTypes.elementType.isRequired,
  onScroll: PropTypes.func,
  children: PropTypes.node,
  resetScrollPosition: PropTypes.bool,
  initialScrollPosition: PropTypes.number,
  propagateEventOnReachTop: PropTypes.bool,
  initialMaxOffset: PropTypes.number,
};

export default memo(VirtualizedList);
